import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '@/api/http';
import { ensureDeviceKey } from '@/api/deviceKey';
import { useApi } from '@/api/provider';
import { queryKeys } from '@/api/queryKeys';
import {
  onSessionSignal,
  revocationReasonOf,
  type ProofFailureReason,
  type RevocationReason,
} from '@/api/sessionSignals';
import { retryDelay, shouldRetryQuery } from '@/api/retry';
import type { LoginRequest, Session, SessionAccount } from '@/api/types';
import {
  bindingSnapshot,
  clearProofFailure,
  initBinding,
  noteBound,
  noteProofFailure,
  refreshBindingKey,
  subscribeBinding,
} from '@/session/binding';
import { createSessionChannel, type SessionChannel } from '@/session/channel';
import { lastUserActivity } from '@/session/activity';
import { pendingActions, type PendingAction } from '@/session/pendingActions';
import { queuedOutboxCount } from '@/features/outbox/purge';
import { setOutboxOwner } from '@/features/outbox/store';
import { reportCachePurge, teardownSession } from '@/session/teardown';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { FolderTreeSkeleton, MessageListSkeleton } from '@/components/Skeleton';
import { isDesktopApp } from '@/utils/desktop';
import { ContentOriginProvider } from '@/features/message-frame/contentOrigin';

/**
 * # Session state without a readable session
 *
 * The three session cookies are `__Host-` and two of them are `HttpOnly`
 * (ADR 0001), so this provider **cannot look at the session**. There is no
 * `isLoggedIn` flag to read, no expiry to parse out of a token, and
 * `document.cookie` tells us nothing except whether a CSRF token happens to be
 * present — which is not the same question and must never be treated as if it
 * were (the CSRF cookie can outlive the session, or be missing while the
 * session is fine).
 *
 * So session state is **derived**, from exactly two observations:
 *
 * 1. **`GET /auth/session`** — the authority. It is the only thing that can
 *    say "these cookies are a live session, and here is whose". It runs once at
 *    boot (`staleTime: Infinity`) and again whenever something suggests the
 *    answer changed: another tab signalled, or this tab came back from being
 *    hidden for a while.
 * 2. **Observed `401`s** — the transport publishes one on any non-`/auth/`
 *    request that comes back unauthorized. That is how we learn the session
 *    died *between* whoami calls, which is the normal case.
 *
 * Everything else is a consequence of that. `expiresAt` from the whoami
 * response lets us predict expiry (useful offline, where observation is
 * impossible), but it is a hint about the server's clock, not a source of
 * truth: the server may revoke early, and the client's clock may be wrong.
 *
 * ## Sliding expiry (ADR 0082)
 *
 * The server moves `expiresAt` forward on any request a person made, and says
 * so in `X-Session-Expires` on every authenticated response. The transport
 * publishes that as an `expiry` signal; this provider moves its copy forward
 * (never back) and tells the other tabs (`refreshed`), so a person working in
 * one tab is not warned in another. Activity that sends no request — typing a
 * long message — is covered here: when the warning window opens and this tab
 * has seen input since the expiry last moved, the provider refreshes quietly
 * instead of showing the banner. The banner is for a person who is truly away.
 *
 * ## The states
 *
 * - `bootstrapping` — the first whoami has not resolved. Render shell chrome
 *   with skeletons; a redirect to `/login` here would flash the login screen on
 *   every cold start.
 * - `authenticated` — whoami answered.
 * - `anonymous` — whoami said 401. There is nothing to preserve, so the guard
 *   redirects to `/login` with `?from=`.
 * - `expired` — we *were* authenticated and then learned we are not. This is
 *   the case a redirect would ruin: a half-written message, a scroll position
 *   and a selection all still exist. Re-auth happens in a modal over the app.
 * - `unavailable` — `503 upstream_failure`, or a cold start that never reached
 *   the backend. Explicitly **not** a sign-out: the session may be perfectly
 *   alive behind a datastore outage, and signing the user out over one would
 *   destroy their work for no reason.
 */
export type SessionStatus =
  | 'bootstrapping'
  | 'authenticated'
  | 'anonymous'
  | 'expired'
  /**
   * Boot found a live cookie and no device key: `GET /auth/session` answered
   * `401 proof_required` with the trio untouched (ADR 0042).
   *
   * Kept apart from `anonymous` because the difference is the whole feature.
   * `anonymous` redirects to `/login`; this must not, or the commonest failure
   * of a 30-day session — the browser reclaimed some storage — would present
   * as a sign-out and throw the session away. It gets the recovery dialog, and
   * cancelling it is what signs the user out.
   */
  | 'recovery'
  | 'unavailable';

/** Why the session is in the `expired` state — the modal reads differently. */
export type ExpiryReason = 'server' | 'offline';

/** What the UI needs to know about binding, flattened for rendering. */
export interface BindingSummary {
  /** The server's verdict. `undefined` before the first `Session` body. */
  bound: boolean | undefined;
  /** This browser can hold a key at all (WebCrypto + usable storage). */
  supported: boolean;
  /** A key exists in this profile right now. */
  hasKey: boolean;
  /**
   * The server refused a request for want of a proof and kept the session.
   * Raises the in-place password dialog; never a sign-out, never a redirect.
   */
  recoveryNeeded: boolean;
  /** Which `proof_*` code was final, so the copy can be specific. */
  recoveryReason: ProofFailureReason | null;
}

/**
 * The question sign-out asks when this device is holding unsent mail.
 *
 * Raised by {@link SessionContextValue.logout} instead of signing out, and
 * answered by the user. It exists because the two obvious implementations are
 * both wrong: deleting the queue silently destroys a message the server has
 * never seen and nothing else holds a copy of, and signing out while leaving it
 * behind hands the next person at a shared machine the previous user's mail
 * (ADR 0058, ADR 0060 §3).
 *
 * Rendered by `components/SignOutOutboxDialog.tsx`, which `RequireSession`
 * mounts — so all three sign-out buttons (header, drawer, re-auth dialog) are
 * gated by one piece of code rather than three copies of it.
 */
export interface SignOutPrompt {
  /** How many unsent messages are held on this device. Always at least one. */
  queued: number;
  /** Sign out, and delete them. The consequence the dialog states plainly. */
  confirm(): Promise<void>;
  /** Stay signed in. Nothing is deleted and the session is untouched. */
  cancel(): void;
}

export interface SessionContextValue {
  status: SessionStatus;
  /**
   * Who the session belongs to, or `undefined` when there is no session.
   *
   * `account`, not `user`: under pass-through auth there is no user record to
   * describe (ADR 0010). The owner *is* the normalised email address, and
   * `account.id` — the primary mailbox's derived id — is what identity
   * comparisons and `AccountProvider`'s default both use. There is no
   * `displayName` here because nothing can populate one; anywhere a name is
   * wanted, the address is the name.
   */
  account: SessionAccount | undefined;
  /**
   * The server says this owner is an administrator (ADR 0030). Drives whether
   * the Admin entry is drawn -- a convenience, never a control: the server
   * re-checks on every `/admin/*` request and answers everyone else `404`.
   */
  admin: boolean;
  /** When the step-up elevation lapses, if one is live (ADR 0032). */
  elevatedUntil: string | null | undefined;
  expiresAt: string | undefined;
  /**
   * Milliseconds until `expiresAt`, sampled on the provider's own clock tick.
   * Exposed so a countdown does not have to call `Date.now()` during render.
   */
  expiresInMs: number | undefined;
  /** True while the backend is answering 503; the session is untouched. */
  serviceDegraded: boolean;
  /** `expiresAt` is within the warning window and the user is idle. */
  expiresSoon: boolean;
  expiryReason: ExpiryReason | undefined;
  /** Set after a re-auth landed on a different account; queued work was dropped. */
  identityChanged: boolean;
  /**
   * Session binding, as the *server* reports it plus what this browser can do
   * (ADR 0042).
   *
   * `bound === false` is a real, working session that is protected by cookies
   * alone; it is shown, not hidden, and it cannot be remembered.
   * `recoveryNeeded` is the case that must never become a redirect: cookies
   * fine, device key gone, everything the user was writing still on screen.
   */
  binding: BindingSummary;
  /**
   * Why the server ended this device's session from somewhere else
   * (`401 session_revoked`), or `null`. Set on the way to the sign-in screen
   * and read by it, so a person whose password was just changed is told that,
   * rather than being shown a password prompt that can only fail. Cleared by
   * the next sign-in, here or in another tab.
   */
  revocation: RevocationReason | null;
  pending: readonly PendingAction[];
  online: boolean;

  login(input: LoginRequest): Promise<Session>;
  /** Re-auth from the modal: same address, password only. */
  reauthenticate(password: string): Promise<void>;
  /**
   * Enrol a device key on this live session (`POST /auth/rebind`).
   *
   * Two callers: the recovery dialog (key lost, session fine) and the unbound
   * banner's *Protect this session*. Nothing is torn down and nothing
   * navigates — that is the entire point.
   */
  rebindDevice(password: string): Promise<void>;
  /**
   * Sign out — *or*, when unsent mail is queued on this device, raise
   * {@link signOutPrompt} and sign out nothing. Callers do not have to know
   * which happened; they fire and forget as they always did.
   */
  logout(): Promise<void>;
  /** Non-null while sign-out is waiting on the user's answer about the Outbox. */
  signOutPrompt: SignOutPrompt | null;
  refresh(): Promise<void>;
  /** Replay queued work. `'automatic'` = sends only. */
  flushPending(scope: 'automatic' | 'all'): Promise<void>;
  discardPending(): void;
  /**
   * Runs a mutation under the session rules: queued (never attempted) when the
   * client already knows the session is unusable, and queued rather than lost
   * when the server says 401 mid-flight.
   */
  runGuarded(action: Omit<PendingAction, 'id' | 'queuedAt'>): Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

/** Warn this long before `expiresAt` so a long compose is never lost silently. */
export const EXPIRY_WARNING_MS = 2 * 60_000;
/** Re-check the clock this often; cheap, and only while authenticated. */
export const CLOCK_TICK_MS = 30_000;
/** `navigator.locks` name that makes a refresh single-flight across tabs. */
const REFRESH_LOCK = 'ols-mail.session-refresh';

/**
 * Runs `fn` holding the cross-tab refresh lock where the browser has one, so
 * five tabs whose warning windows open together send one refresh, not five —
 * the others find the expiry already moved (by the `refreshed` broadcast) when
 * their turn comes. Without `navigator.locks` it simply runs.
 */
async function withRefreshLock(fn: () => Promise<void>): Promise<void> {
  if (typeof navigator !== 'undefined' && 'locks' in navigator && navigator.locks) {
    await navigator.locks.request(REFRESH_LOCK, fn);
    return;
  }
  await fn();
}
/** Hidden longer than this and the session is revalidated on return. */
const REVALIDATE_AFTER_HIDDEN_MS = 5 * 60_000;

export function SessionProvider({
  children,
  channel: injectedChannel,
}: {
  children: ReactNode;
  /** Tests inject a channel; the app builds its own. */
  channel?: SessionChannel;
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const online = useOnlineStatus();

  const [expired, setExpired] = useState(false);
  const [degraded, setDegraded] = useState(false);
  const [identityChanged, setIdentityChanged] = useState(false);
  const [pending, setPending] = useState<readonly PendingAction[]>(() => pendingActions.list());
  const [now, setNow] = useState(() => Date.now());
  /**
   * The last input this tab saw, sampled on the clock tick — read from the
   * activity tracker there rather than during render, which must stay pure.
   */
  const [activityAt, setActivityAt] = useState(() => lastUserActivity());
  /**
   * When this tab last learned of a later expiry: a slide, a refresh here or
   * in another tab, a sign-in. Input after it is activity the server has not
   * counted yet.
   */
  const [extendedAt, setExtendedAt] = useState(() => Date.now());
  /** A quiet refresh for an active user failed; the banner shows after all. */
  const [autoRefreshFailed, setAutoRefreshFailed] = useState(false);
  /** One refresh at a time from this tab; the lock covers the other tabs. */
  const refreshInFlight = useRef<Promise<void> | null>(null);
  /**
   * How many unsent messages sign-out is waiting on an answer about, or `null`
   * when no question is open. A number rather than a boolean because the
   * dialog's whole job is to say *how many* the user is about to lose.
   */
  const [signOutQueued, setSignOutQueued] = useState<number | null>(null);
  const [revocation, setRevocation] = useState<RevocationReason | null>(null);
  /**
   * Latched on the first `session_revoked`, synchronously, because the same
   * news arrives many times over: every request in flight, the event stream's
   * ticket mint, the whoami refetch the teardown itself provokes, and the
   * other tabs. Signing out once is the job; the rest must be no-ops, or the
   * teardown would provoke the whoami that re-triggers the teardown.
   */
  const revokedRef = useRef(false);

  const query = useQuery<Session>({
    queryKey: queryKeys.session(),
    queryFn: ({ signal }) => api.auth.session(signal),
    staleTime: Number.POSITIVE_INFINITY,
    // A 401 is an answer and is never retried (see api/retry.ts); a 503 or a
    // failed connection is retried, because "the datastore is down" must not
    // resolve to "signed out".
    retry: shouldRetryQuery,
    retryDelay,
  });

  const session = query.data;
  const error = query.error;

  // Created once per provider, lazily: a channel opened during render and
  // thrown away on the next one would leak a BroadcastChannel per render.
  const [channel] = useState<SessionChannel>(() => injectedChannel ?? createSessionChannel());

  useEffect(() => {
    // Only close a channel this provider created; an injected one is the
    // caller's to close.
    if (injectedChannel !== undefined) return;
    return () => channel.close();
  }, [channel, injectedChannel]);

  useEffect(() => pendingActions.subscribe(setPending), []);

  // The Outbox shows and sends only the signed-in owner's mail (ADR 0061 §4).
  // `null` while signed out: a queue kept after a revocation stays hidden
  // until its own owner is back.
  const ownerId = query.data?.account.id ?? null;
  useEffect(() => {
    void setOutboxOwner(ownerId);
  }, [ownerId]);

  /* ------------------------------------------------------- device binding */

  const binding = useSyncExternalStore(subscribeBinding, bindingSnapshot, bindingSnapshot);

  // Generated once per profile, at boot. On the login page this is ADR 0044's
  // "before submit, off the submit path"; on an existing session that lost its
  // key it is what the rebind will enrol. Both want exactly this call, and it
  // is milliseconds.
  useEffect(() => {
    void initBinding();
  }, []);

  // The server's verdict, recorded whenever a `Session` body arrives. Never
  // inferred from holding a key — that inference is the silent fallback.
  useEffect(() => {
    noteBound(session?.bound);
  }, [session?.bound]);

  /* ------------------------------------------- observation: transport signals */

  const hasSession = session !== undefined;

  /**
   * "Did we believe we were signed in?", asked from a callback.
   *
   * Read out of the query cache rather than mirrored into a ref: the cache
   * entry *is* the derived session state, so a ref would be a second copy of
   * it that can disagree — and it would be read during render, which is
   * exactly how a stale copy happens.
   */
  const believedSignedIn = useCallback(
    () => queryClient.getQueryData(queryKeys.session()) !== undefined,
    [queryClient],
  );

  /**
   * The server says the session now ends at `expiresAt`. Moves this tab's copy
   * forward — never back: responses arrive out of order, and the expiry only
   * ever moves back by revocation, which is a 401 and not this — and, when the
   * news came from this tab's own requests, tells the other tabs.
   */
  const advanceExpiry = useCallback(
    (expiresAt: string, broadcast: boolean) => {
      const next = Date.parse(expiresAt);
      if (!Number.isFinite(next)) return;
      let advanced = false;
      queryClient.setQueryData<Session>(queryKeys.session(), (previous) => {
        if (previous === undefined) return previous;
        const current = Date.parse(previous.expiresAt);
        if (Number.isFinite(current) && next <= current) return previous;
        advanced = true;
        return { ...previous, expiresAt };
      });
      if (!advanced) return;
      const at = Date.now();
      setExtendedAt(at);
      setNow(at);
      setAutoRefreshFailed(false);
      if (broadcast) channel.postRefreshed(expiresAt);
    },
    [channel, queryClient],
  );

  const applyLogout = useCallback(
    (options: { navigate?: boolean; keepOutbox?: boolean } = {}) => {
      setExpired(false);
      // A `logout` broadcast from another tab lands here too. If this tab had
      // the Outbox question open, it is now moot — the queue is going with the
      // session the other tab ended — so the dialog must not outlive it and
      // sit over the login screen.
      setSignOutQueued(null);
      // The dialog belongs to a session that no longer exists; leaving the flag
      // set would show a recovery prompt over the login page.
      clearProofFailure();
      // `teardownSession` deletes the device key (ADR 0044, step 5): key
      // lifetime is session lifetime, so no thumbprint outlives its owner on a
      // shared machine.
      // Not awaited: the user leaves for /login now. The purge runs to its own
      // conclusion in the worker, and `reportCachePurge` says so honestly if
      // the cached API reads outlived the sign-out.
      //
      // `keepOutbox` (a revocation, ADR 0061 §4): the unsent mail stays on this
      // device for the same owner's next sign-in. Its owner is read here,
      // before the teardown clears the query cache that holds the session.
      const keepOutbox =
        options.keepOutbox === true
          ? {
              owner: queryClient.getQueryData<Session>(queryKeys.session())?.account.id,
            }
          : undefined;
      void teardownSession(queryClient, {
        discardQueuedWork: true,
        ...(keepOutbox !== undefined ? { keepOutbox } : {}),
      }).then(reportCachePurge);
      if (options.navigate !== false) navigate('/login', { replace: true });
    },
    [navigate, queryClient],
  );

  /**
   * `401 session_revoked`: the owner ended this session from elsewhere — by
   * changing the password, or with "Sign out other sessions" (ADR 0045).
   *
   * A sign-out, not an expiry. The record is already deleted, so the re-auth
   * dialog `expired` raises would be a password prompt over a session that
   * cannot come back, and — after a password change — one the user may well
   * answer with the old password. So: the same teardown an explicit sign-out
   * runs, then the sign-in screen, which says why — with one exception, the
   * Outbox. Unsent mail is kept for its owner and sends after they sign back
   * in (owner decision 2026-09-22, ADR 0061 §4); nobody was asked whether to
   * throw it away, so it is not thrown away.
   *
   * `fromServer` is this tab hearing it first. Only then does it tell the
   * other tabs (with the reason, so every sign-in screen says the same thing)
   * and ask the server to clear the dead cookie trio, which the revoking
   * response deliberately leaves in place.
   */
  const applyRevocation = useCallback(
    (reason: RevocationReason, fromServer: boolean) => {
      if (revokedRef.current) return;
      revokedRef.current = true;
      setRevocation(reason);
      // Signed in: leave for /login now. Not signed in — a cold load whose
      // whoami said `session_revoked` — the guard is already redirecting with
      // `?from=`, and a second navigation here would throw the return path
      // away.
      const signedIn = believedSignedIn();
      if (fromServer) {
        channel.post('revoked', reason);
        api.auth.logout().catch(() => {
          // Best effort: the record is gone either way, and the dead cookies
          // can only ever earn another `session_revoked`.
        });
      }
      applyLogout({ navigate: signedIn, keepOutbox: true });
    },
    [api, applyLogout, believedSignedIn, channel],
  );

  useEffect(
    () =>
      onSessionSignal((signal) => {
        switch (signal.type) {
          case 'proof':
            // A live session refusing one request. `setExpired` is deliberately
            // NOT called: the cookies are intact, the record is alive, and the
            // cache and the half-written reply behind this dialog are all
            // still valid (ADR 0042).
            noteProofFailure(signal.reason);
            break;
          case 'revoked':
            // Before the generic 401 in the transport, and handled as a
            // sign-out here: never `setExpired`, so no re-auth dialog.
            applyRevocation(signal.reason, true);
            break;
          case 'expired':
            // Only meaningful if we believed we were signed in. A 401 while
            // already anonymous is just the guard's job. After a revocation
            // it is the same dead session saying so again.
            if (revokedRef.current) break;
            if (believedSignedIn()) {
              setExpired(true);
              channel.post('expired');
            }
            break;
          case 'unavailable':
            setDegraded(true);
            break;
          case 'available':
            setDegraded(false);
            break;
          case 'expiry':
            // The server slid (or merely restated) the expiry on a response
            // to one of this tab's requests.
            if (!revokedRef.current) advanceExpiry(signal.expiresAt, true);
            break;
        }
      }),
    [advanceExpiry, applyRevocation, believedSignedIn, channel],
  );

  // The same news through the session query itself: a cold load on the
  // revoked device, or the revalidation after a long hide. The transport has
  // usually published it already; the latch makes this a no-op then, and the
  // right answer for an adapter that publishes nothing (the mock).
  useEffect(() => {
    if (error instanceof ApiError && error.isSessionRevoked) {
      applyRevocation(revocationReasonOf(error.details), true);
    }
  }, [applyRevocation, error]);

  /* ------------------------------------------------- the clock (offline case) */

  useEffect(() => {
    if (!hasSession) return;
    // Both samples in one tick, so they render together: a banner decided on
    // a fresh clock and a stale activity sample would flash for an active
    // user before the quiet refresh hid it.
    const timer = setInterval(() => {
      setNow(Date.now());
      setActivityAt(lastUserActivity());
    }, CLOCK_TICK_MS);
    return () => clearInterval(timer);
  }, [hasSession]);

  const expiresAtMs = session === undefined ? undefined : Date.parse(session.expiresAt);
  const clockSaysExpired =
    expiresAtMs !== undefined && Number.isFinite(expiresAtMs) && expiresAtMs <= now;

  // Offline + past expiry is the one case the client can call on its own: no
  // request can succeed, so attempting mutations would only lose them. We say
  // so, keep cached mail readable, and queue instead of attempting.
  const offlineExpired = !online && clockSaysExpired;

  // Derived, not mirrored into state: an effect that copies one piece of state
  // into another is a render cascade *and* a chance for the two to disagree.
  const isExpired = expired || offlineExpired;
  const expiryReason: ExpiryReason | undefined = isExpired
    ? offlineExpired
      ? 'offline'
      : 'server'
    : undefined;

  const inWarningWindow =
    !isExpired &&
    expiresAtMs !== undefined &&
    Number.isFinite(expiresAtMs) &&
    expiresAtMs - now <= EXPIRY_WARNING_MS &&
    expiresAtMs > now;
  // Input the server has not counted: a person is here, just not sending
  // anything. They get a quiet refresh (below), not a warning.
  const activeSinceExtension = activityAt > extendedAt;
  const expiresSoon = inWarningWindow && (!activeSinceExtension || autoRefreshFailed);

  /* ------------------------------------------------------------ cross-tab */

  const refetchSession = query.refetch;

  useEffect(
    () =>
      channel.subscribe((message) => {
        switch (message.type) {
          case 'logout':
            applyLogout();
            break;
          case 'revoked':
            // Another tab heard `session_revoked` first. Same sign-out, same
            // reason on this tab's sign-in screen; the other tab has already
            // asked the server to clear the cookies.
            applyRevocation(message.reason ?? 'unknown', false);
            break;
          case 'login':
          case 'reauthenticated':
            // Another tab fixed the session. Clear our own prompt and re-derive
            // from the authority rather than trusting the message.
            revokedRef.current = false;
            setRevocation(null);
            setExpired(false);
            clearProofFailure();
            // The other tab may have enrolled a *different* key (a rebind).
            // Drop the cached handle so the next request signs with whatever
            // IndexedDB now holds — the key never crosses the channel, only
            // the news that it changed.
            void refreshBindingKey();
            void refetchSession();
            break;
          case 'expired':
            if (!revokedRef.current && believedSignedIn()) setExpired(true);
            break;
          case 'refreshed':
            // Another tab's user is active, or pressed "Stay signed in".
            // Not re-broadcast: that tab already told everyone.
            if (message.expiresAt !== undefined && !revokedRef.current) {
              advanceExpiry(message.expiresAt, false);
            }
            break;
        }
      }),
    [advanceExpiry, applyLogout, applyRevocation, believedSignedIn, channel, refetchSession],
  );

  /* --------------------------------------- revalidate after a long hide */

  useEffect(() => {
    if (typeof document === 'undefined') return;
    let hiddenSince: number | null = document.visibilityState === 'hidden' ? Date.now() : null;
    const onVisibility = (): void => {
      if (document.visibilityState === 'hidden') {
        hiddenSince = Date.now();
        return;
      }
      const away = hiddenSince === null ? 0 : Date.now() - hiddenSince;
      hiddenSince = null;
      setNow(Date.now());
      setActivityAt(lastUserActivity());
      if (away >= REVALIDATE_AFTER_HIDDEN_MS) void refetchSession();
    };
    // Back online: the session may have ended, or been slid by another
    // device-less tab, while nothing could be heard. Whoami answers both and
    // slides nothing itself.
    const onOnline = (): void => {
      if (believedSignedIn()) void refetchSession();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', onOnline);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', onOnline);
    };
  }, [believedSignedIn, refetchSession]);

  /* ---------------------------------------------------------------- actions */

  const login = useCallback(
    async (input: LoginRequest): Promise<Session> => {
      // Enrol this browser's key with the login (ADR 0040). Already generated
      // at boot in the normal case, so this resolves immediately; when the
      // browser cannot hold one at all the field is simply absent and the
      // *server* decides what that is worth — `preferred` issues an unbound
      // session, `required` refuses with `device_key_required`.
      const key = await ensureDeviceKey();
      const enrolment: LoginRequest =
        key.status === 'ready'
          ? { ...input, deviceKey: key.key.jwk, app: isInstalledApp() }
          : input;
      const next = await api.auth.login(enrolment);
      queryClient.setQueryData(queryKeys.session(), next);
      setExpired(false);
      setDegraded(false);
      setIdentityChanged(false);
      revokedRef.current = false;
      setRevocation(null);
      clearProofFailure();
      noteBound(next.bound);
      channel.post('login');
      return next;
    },
    [api, channel, queryClient],
  );

  const reauthenticate = useCallback(
    async (password: string): Promise<void> => {
      // `account.id` is a pure function of the normalised email (ADR 0010), so
      // comparing it answers "is this the same person?" without a user table.
      const previous = session?.account.id;

      // Enrol the device key here too, exactly as `login` does. This call is a
      // login in every sense the server cares about, and omitting the key made
      // it the one way into the app that could not satisfy `session.binding:
      // required` -- so confirming your password, the remedy the app offers
      // when a session is refused, was itself refused with
      // `device_key_required`. A browser whose key is already enrolled sends
      // the same JWK again, which the server treats as the same device.
      const key = await ensureDeviceKey();
      const credentials: LoginRequest = { email: session?.account.email ?? '', password };
      const next = await api.auth.login(
        key.status === 'ready'
          ? { ...credentials, deviceKey: key.key.jwk, app: isInstalledApp() }
          : credentials,
      );

      if (previous !== undefined && next.account.id !== previous) {
        // A different person is now signed in on this tab. Their session must
        // not inherit the previous user's cache, and it must certainly not
        // replay the previous user's queued archives.
        // Awaited: a different person is about to use this tab, so the new
        // session must not start before the old one's cached reads are gone.
        reportCachePurge(await teardownSession(queryClient, { discardQueuedWork: true }));
        setIdentityChanged(true);
      } else {
        setIdentityChanged(false);
      }

      queryClient.setQueryData(queryKeys.session(), next);
      setExpired(false);
      setDegraded(false);
      // The new session carries this browser's key, so whatever proof failure
      // sent the user here is answered. Leaving it set would keep the "confirm
      // your password" prompt up over a session that is already bound.
      clearProofFailure();
      channel.post('reauthenticated');

      // Everything the user was looking at refetches; nothing they were typing
      // is touched, because nothing unmounted.
      await queryClient.invalidateQueries({ refetchType: 'active' });
      // A send is replayed without asking; anything else waits for the user.
      await pendingActions.flush('automatic');
    },
    [api, channel, queryClient, session],
  );

  const rebindDevice = useCallback(
    async (password: string): Promise<void> => {
      // Generate first: the request is signed by the *new* key and the server
      // verifies that signature against the JWK in the body, so the key has to
      // exist before the call rather than after it.
      const key = await ensureDeviceKey();
      if (key.status !== 'ready') {
        throw new Error(
          'This browser cannot store a session key, so it cannot be protected on this device.',
        );
      }
      const next = await api.auth.rebind({ password, deviceKey: key.key.jwk });

      queryClient.setQueryData(queryKeys.session(), next);
      clearProofFailure();
      noteBound(next.bound);
      // Other tabs drop their cached handle and carry on; nothing here
      // unmounts, so the draft behind this dialog is untouched.
      channel.post('reauthenticated');
      // Whatever was refused while the key was missing is worth another try.
      await queryClient.invalidateQueries({ refetchType: 'active' });
      await pendingActions.flush('automatic');
    },
    [api, channel, queryClient],
  );

  /** The sign-out itself, once there is nothing left to ask about. */
  const signOutNow = useCallback(async (): Promise<void> => {
    setSignOutQueued(null);
    try {
      await api.auth.logout();
    } catch {
      // The cookies may already be gone. Tear down locally regardless — the
      // dangerous outcome is a cache that outlives the session, not a failed
      // logout call.
    }
    channel.post('logout');
    applyLogout();
  }, [api, applyLogout, channel]);

  /**
   * Sign out — after asking, when this device holds unsent mail.
   *
   * The count is read from IndexedDB rather than from the Outbox snapshot: the
   * snapshot is filled lazily by the first subscriber, so a tab that has not
   * drawn the Outbox badge yet would report an empty queue for a full one and
   * the messages would be deleted with no question asked.
   *
   * Nothing is torn down on this path when the answer is "ask". The server
   * session is still alive, the cookies are untouched, and the user can go and
   * send or delete the messages themselves — which is the whole point of
   * offering the choice rather than announcing the loss.
   */
  const logout = useCallback(async (): Promise<void> => {
    const queued = await queuedOutboxCount();
    if (queued > 0) {
      setSignOutQueued(queued);
      return;
    }
    await signOutNow();
  }, [signOutNow]);

  const cancelSignOut = useCallback((): void => setSignOutQueued(null), []);

  const signOutPrompt = useMemo<SignOutPrompt | null>(
    () =>
      signOutQueued === null
        ? null
        : { queued: signOutQueued, confirm: signOutNow, cancel: cancelSignOut },
    [cancelSignOut, signOutNow, signOutQueued],
  );

  /**
   * `POST /auth/refresh`, single-flight: one at a time from this tab, and one
   * at a time across tabs where the browser has `navigator.locks`.
   *
   * `unlessPast` is the quiet refresh's guard: when the expiry already moved
   * past it by the time the lock is ours — another tab refreshed and
   * broadcast while this one waited — there is nothing left to do. *Stay
   * signed in* passes nothing and always refreshes: the user asked.
   */
  const refreshOnce = useCallback(
    (unlessPast?: number): Promise<void> => {
      if (refreshInFlight.current !== null) return refreshInFlight.current;
      const run = async (): Promise<void> => {
        if (unlessPast !== undefined) {
          const current = queryClient.getQueryData<Session>(queryKeys.session());
          const currentMs = current === undefined ? Number.NaN : Date.parse(current.expiresAt);
          if (Number.isFinite(currentMs) && currentMs > unlessPast) return;
        }
        const next = await api.auth.refresh();
        queryClient.setQueryData(queryKeys.session(), next);
        const at = Date.now();
        setExtendedAt(at);
        setNow(at);
        // At the absolute cap a refresh cannot move the expiry. The quiet
        // path then stops trying and lets the banner say so, rather than
        // refreshing on every tick until the session ends mid-sentence.
        setAutoRefreshFailed(unlessPast !== undefined && !(Date.parse(next.expiresAt) > unlessPast));
        channel.postRefreshed(next.expiresAt);
      };
      const pending = withRefreshLock(run).finally(() => {
        refreshInFlight.current = null;
      });
      refreshInFlight.current = pending;
      return pending;
    },
    [api, channel, queryClient],
  );

  const refresh = useCallback((): Promise<void> => refreshOnce(), [refreshOnce]);

  // The quiet refresh: the warning window has opened on a person who is
  // here. Their input never reached the server, so the server is about to
  // end a session that is in use; refreshing is what they would do if we
  // asked, so we do not ask. A failure shows the banner after all, with its
  // own button to try again.
  useEffect(() => {
    if (!inWarningWindow || !activeSinceExtension || autoRefreshFailed || !online) return;
    if (expiresAtMs === undefined) return;
    refreshOnce(expiresAtMs).catch(() => setAutoRefreshFailed(true));
  }, [activeSinceExtension, autoRefreshFailed, expiresAtMs, inWarningWindow, online, refreshOnce]);

  const flushPending = useCallback(async (scope: 'automatic' | 'all'): Promise<void> => {
    await pendingActions.flush(scope);
  }, []);

  const discardPending = useCallback((): void => {
    pendingActions.discardAll();
  }, []);

  const runGuarded = useCallback(
    async (action: Omit<PendingAction, 'id' | 'queuedAt'>): Promise<void> => {
      // Known-dead session: queue without attempting. Firing the request would
      // burn the work on a guaranteed 401 (and, offline, on nothing at all).
      if (isExpired) {
        pendingActions.enqueue(action);
        return;
      }
      try {
        await action.run();
      } catch (cause) {
        if (cause instanceof ApiError && cause.isSessionExpired) {
          pendingActions.enqueue(action);
          return;
        }
        throw cause;
      }
    },
    [isExpired],
  );

  /* ----------------------------------------------------------- derived state */

  const status: SessionStatus = useMemo(() => {
    if (session !== undefined) return isExpired ? 'expired' : 'authenticated';
    if (query.isPending) return 'bootstrapping';
    // Dispatched on the code before the generic 401: the cookies are alive and
    // the record is kept, so this is a password prompt, not a sign-out.
    if (error instanceof ApiError && error.isProofFailure) return 'recovery';
    if (error instanceof ApiError && error.isSessionExpired) return 'anonymous';
    // Any other bootstrap failure — 503, a dead connection, a proxy error — is
    // "we do not know", and "we do not know" is not "signed out".
    if (error !== null) return 'unavailable';
    return 'anonymous';
  }, [error, isExpired, query.isPending, session]);

  const serviceDegraded =
    degraded || (error instanceof ApiError && error.isServiceUnavailable) || status === 'unavailable';

  const bindingSummary = useMemo<BindingSummary>(
    () => ({
      bound: binding.bound ?? session?.bound,
      supported: binding.key === undefined || binding.key.status !== 'unsupported',
      hasKey: binding.key?.status === 'ready',
      // Only ever raised while we believe there *is* a session to recover.
      // A proof failure with no session is the anonymous path's problem.
      // While signed in, the dialog is raised by an observed `proof_*` 401.
      // At boot there is no session yet and `status: 'recovery'` carries it
      // instead — see the guard.
      recoveryNeeded: binding.proofFailure !== null && session !== undefined,
      recoveryReason: binding.proofFailure,
    }),
    [binding, session],
  );

  const value = useMemo<SessionContextValue>(
    () => ({
      status,
      account: session?.account,
      admin: session?.admin === true,
      elevatedUntil: session?.elevatedUntil,
      expiresAt: session?.expiresAt,
      expiresInMs:
        expiresAtMs === undefined || !Number.isFinite(expiresAtMs)
          ? undefined
          : Math.max(0, expiresAtMs - now),
      serviceDegraded,
      expiresSoon,
      expiryReason,
      identityChanged,
      binding: bindingSummary,
      revocation,
      pending,
      online,
      login,
      reauthenticate,
      rebindDevice,
      logout,
      signOutPrompt,
      refresh,
      flushPending,
      discardPending,
      runGuarded,
    }),
    [
      bindingSummary,
      discardPending,
      expiresAtMs,
      expiryReason,
      expiresSoon,
      now,
      flushPending,
      identityChanged,
      login,
      logout,
      online,
      pending,
      revocation,
      signOutPrompt,
      reauthenticate,
      rebindDevice,
      refresh,
      runGuarded,
      serviceDegraded,
      session,
      status,
    ],
  );

  return (
    <SessionContext.Provider value={value}>
      {/* ADR 0078: where message bodies are framed from, when anywhere. */}
      <ContentOriginProvider origin={session?.contentOrigin}>{children}</ContentOriginProvider>
    </SessionContext.Provider>
  );
}

export function useSession(): SessionContextValue {
  const value = useContext(SessionContext);
  if (value === null) throw new Error('useSession must be used inside <SessionProvider>');
  return value;
}

/**
 * The session, or `null` when there is no provider above.
 *
 * For the handful of providers that are legitimately mountable without the
 * session layer — `AccountProvider` in a component test, the settings tree in
 * isolation — where "no session context" is a real configuration and not a
 * bug. Everything that *needs* a session uses {@link useSession} and throws.
 */
export function useOptionalSession(): SessionContextValue | null {
  return useContext(SessionContext);
}

/**
 * Shell chrome while the first whoami resolves.
 *
 * Skeletons, not a spinner and not a blank page: the chrome is precached, so
 * this is the moment the offline shell earns its keep.
 */
export function SessionBootSkeleton() {
  return (
    <div className="pane" style={{ padding: 'var(--space-4)', gap: 'var(--space-4)' }} aria-busy="true">
      <span className="visually-hidden">Checking your session…</span>
      <FolderTreeSkeleton rows={5} />
      <MessageListSkeleton rows={8} />
    </div>
  );
}

/**
 * True when the app is running as an installed PWA, or inside the desktop app
 * (ADR 0051), which is installed just the same.
 *
 * Sent with the login so the session list can say *app* rather than showing a
 * browser name that means nothing on a home screen icon (ADR 0045). A hint,
 * like everything else in that row — never an identifier.
 */
function isInstalledApp(): boolean {
  if (isDesktopApp()) return true;
  try {
    return window.matchMedia('(display-mode: standalone)').matches;
  } catch {
    // jsdom and older engines have no matchMedia; "not an installed app" is
    // the honest answer when we cannot tell.
    return false;
  }
}
